import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const QRCodeScanner = ({ onScanResult, onClose, isActive = false }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const startScanning = async () => {
    try {
      setError(null);
      setIsScanning(true);

      const stream = await navigator.mediaDevices?.getUserMedia({
        video: { 
          facingMode: 'environment',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      streamRef.current = stream;
      if (videoRef?.current) {
        videoRef.current.srcObject = stream;
      }
      setHasPermission(true);

      // Simulate QR code detection (in real implementation, use a QR library)
      setTimeout(() => {
        const mockEmployeeCode = "123456";
        onScanResult(mockEmployeeCode);
        stopScanning();
      }, 3000);

    } catch (err) {
      setError('Zugriff auf Kamera verweigert oder nicht verfügbar');
      setHasPermission(false);
      setIsScanning(false);
    }
  };

  const stopScanning = () => {
    if (streamRef?.current) {
      streamRef?.current?.getTracks()?.forEach(track => track?.stop());
      streamRef.current = null;
    }
    setIsScanning(false);
  };

  useEffect(() => {
    if (isActive) {
      startScanning();
    } else {
      stopScanning();
    }

    return () => {
      stopScanning();
    };
  }, [isActive]);

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-xl p-6 max-w-md w-full elevation-3">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="QrCode" size={32} className="text-primary" />
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">
            QR-Code-Scanner
          </h2>
          <p className="text-text-secondary">
            Halten Sie den Ausweis-QR-Code in das Kamerafenster
          </p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-2">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <span className="text-sm text-error">{error}</span>
            </div>
          </div>
        )}

        <div className="relative mb-6">
          {isScanning ? (
            <div className="aspect-video bg-muted rounded-lg overflow-hidden relative">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-48 h-48 border-2 border-primary rounded-lg relative">
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-primary rounded-tl-lg"></div>
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-primary rounded-tr-lg"></div>
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-primary rounded-bl-lg"></div>
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-primary rounded-br-lg"></div>
                </div>
              </div>
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-background/80 backdrop-blur-sm rounded-full px-3 py-1">
                  <span className="text-sm text-text-primary">Scannen...</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="aspect-video bg-muted rounded-lg flex items-center justify-center">
              <div className="text-center">
                <Icon name="Camera" size={48} className="text-text-secondary mx-auto mb-2" />
                <p className="text-text-secondary">Kamera nicht aktiv</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex space-x-3">
          {!isScanning ? (
            <Button
              variant="default"
              size="lg"
              fullWidth
              onClick={startScanning}
              iconName="Camera"
              iconPosition="left"
              className="touch-feedback"
            >
              Scan starten
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="lg"
              fullWidth
              onClick={stopScanning}
              iconName="Square"
              iconPosition="left"
              className="touch-feedback"
            >
              Scan stoppen
            </Button>
          )}
          
          <Button
            variant="outline"
            size="lg"
            onClick={onClose}
            iconName="X"
            className="touch-feedback"
          >
            Schließen
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeScanner;